import { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MicIcon from '@mui/icons-material/Mic';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useStoreByName } from '../../hooks/use-stores.hooks';
import SiddurBreadcrumb from '../components/siddur/SiddurBreadcrumb';
import SiddurScreenView from '../components/siddur/SiddurScreenView';
import SiddurReadingView from '../components/siddur/SiddurReadingView';
import SiddurSidePanel from '../components/siddur/SiddurSidePanel';
import Legend from '../components/core/legend';
import MicIndicator from '../components/speech/MicIndicator';

/**
 * The card the reader lives in.
 *
 * It carries its own background and sits above the page's decorative circle.
 * The first version laid the text straight onto the page, and the artwork
 * painted over it: the words were there and unreadable, which is worse than
 * an error, because nothing looks wrong.
 */
const card = {
  position: 'relative' as const,
  zIndex: 2,
  maxWidth: { xs: 980, md: 1180 },
  mx: { xs: 1, sm: 2, md: 'auto' },
  my: { xs: 1, md: 3 },
  borderRadius: '20px',
  overflow: 'hidden',
  backgroundColor: '#fff',
  // The card never grows past the viewport: the top bar's padding, the margins
  // the rest. Only the text box scrolls, so the arrows and the microphone
  // button stay on screen on a phone as much as on a laptop.
  display: 'flex',
  flexDirection: 'column' as const,
  // The container's top padding is 60px on phones and 80px from 480px up.
  maxHeight: { xs: 'calc(100dvh - 60px - 16px)', sm: 'calc(100dvh - 80px - 16px)', md: 'calc(100dvh - 80px - 48px)' },
};

const SiddurReaderPage = () => {
  const { t } = useTranslation();
  const { showByHeartOption } = useStoreByName('uiStore');
  const store = useStoreByName('siddurStore');
  const {
    load,
    isLoading,
    error,
    tree,
    next,
    previous,
    hasNext,
    hasPrevious,
    isReading,
    readingMode,
    screenIndex,
    readRun,
    startReading,
    stopReading,
    currentScreenReading,
  } = store;

  useEffect(() => {
    load();
  }, [load]);

  // While a prayer is on screen and nobody is reading yet, open the microphone
  // and the recogniser ahead of the read button, as the book screens do. The
  // screen index is a dependency so moving with the arrows prepares again after
  // an unused opening was closed. Nothing is sent until the reading starts.
  const { prepareToListen, releaseMicrophone } = useStoreByName('speechRecognitionStore');
  useEffect(() => {
    if (isReading || !currentScreenReading) return;
    prepareToListen?.().catch((error: unknown) => {
      console.warn('Could not prepare the microphone:', error);
    });
  }, [isReading, currentScreenReading, screenIndex, prepareToListen]);

  // Leaving the page closes whatever was opened ahead of time.
  useEffect(() => {
    return () => {
      releaseMicrophone?.().catch((error: unknown) => {
        console.warn('Could not release the microphone:', error);
      });
    };
  }, [releaseMicrophone]);

  // The book pages load the user on the home page first; a reader who lands
  // here directly has no personal info yet, so the star total showed 0 and
  // every save started its count from zero. Load it once here.
  const userStore = useStoreByName('userStore');
  useEffect(() => {
    if (!userStore.userData?.personalInfo && localStorage.getItem('access_token')) {
      userStore.getUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <Box sx={{ ...card, p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (isLoading || !tree) {
    return (
      <Paper elevation={3} sx={{ ...card, p: 3 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={44} />
        ))}
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={card} className="siddur-reader">
      {/* On a phone the breadcrumb already says סידור אשכנז on its first
          crumb, and 60px is a line and a half of large text. */}
      <Box
        sx={{
          backgroundColor: '#3f7cf6',
          color: '#fff',
          px: 3,
          py: 1.5,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <MenuBookIcon />
        <Typography variant="h6" fontWeight={600}>
          {tree.book_name_he}
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.25, md: 2 },
          flex: 1,
          minHeight: 0,
        }}
      >
        <SiddurBreadcrumb />

        {/* The words, given the room they deserve. Everything else on this
            screen is a control; this is the only part a student reads. On a
            wide screen the controls sit beside the text; on a phone they are
            in the menu and the text has the width to itself. */}
        {/* The row carries the minimum height, not the text box inside it:
            a child taller than its flex parent spills out under whatever
            comes next, which is how the key and the microphone button once
            ended up drawn over the words on a phone. */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            alignItems: 'stretch',
            flex: 1,
            // Five lines at the reader's chosen text size, whatever it is.
            minHeight: { xs: 'calc(200px * var(--reading-scale, 1))', md: 320 },
          }}
        >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            backgroundColor: '#fffdf8',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '14px',
            px: { xs: 2, sm: 4 },
            py: 3,
          }}
        >
          {isReading ? (
            // Keyed on the screen so moving to the next one starts a fresh
            // reading instead of carrying the last one's words across.
            <SiddurReadingView key={`${screenIndex}-${readingMode}-${readRun}`} />
          ) : (
            <SiddurScreenView />
          )}
        </Box>
        <SiddurSidePanel />
        </Box>

        <Legend />

        {/* Read it, or say it from memory. No setup: a student lands on a
            prayer and starts.

            בעל פה is behind the same hamburger switch the book reader uses,
            so a teacher turns it on once for the whole app rather than
            meeting it in two places that disagree. Off by default: most
            classes read from the screen, and a second button on the way to
            the microphone is a question a beginner does not need. */}
        {!isReading && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            {/* Yellow while the microphone is open ahead of the button: get ready. */}
            <MicIndicator />
            <Button
              variant="contained"
              startIcon={<MicIcon />}
              disabled={!currentScreenReading}
              onClick={() => startReading('reading')}
            >
              {t('readFromScreen')}
            </Button>
            {showByHeartOption && (
              <Button
                variant="outlined"
                startIcon={<MicIcon />}
                disabled={!currentScreenReading}
                onClick={() => startReading('byHeart')}
              >
                {t('byHeart')}
              </Button>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* The arrows stay live during a reading: pressing one ends it
              and moves. A reading the recogniser never closes must not lock
              the reader on the screen. */}
          <IconButton
            onClick={() => { if (isReading) stopReading(); previous(); }}
            disabled={!hasPrevious}
            aria-label="הקודם"
            sx={{ border: '1px solid rgba(0,0,0,0.12)' }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>

          <IconButton
            onClick={() => { if (isReading) stopReading(); next(); }}
            disabled={!hasNext}
            aria-label="הבא"
            sx={{ border: '1px solid rgba(0,0,0,0.12)' }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default observer(SiddurReaderPage);
